import { Endpoints, Http, HttpService } from "@/infra/api";
import { LocationEntity } from "@/domain/entities";
import { Location } from "@/domain/models";
import {
  ApiResponse,
  ApiQuery,
  LocationDTO,
  EncodeTypes,
  LocationImageDTO,
  LocationImageResponse,
} from "@/@types";
import { Amenity } from "@/domain/models/Amenity";

export interface LocationsRepository {
  getLocations: (query?: ApiQuery) => Promise<ApiResponse<Location[]>>;
  getLocation: (id: string) => Promise<ApiResponse<Location>>;
  createLocation: (params: LocationDTO) => Promise<ApiResponse<Location>>;
  updateLocation: (
    id: string,
    params: Partial<LocationDTO>
  ) => Promise<ApiResponse<Location>>;
  addLocationImage: (
    params: LocationImageDTO
  ) => Promise<ApiResponse<LocationImageResponse>>;
  removeLocationImage: (
    id: string
  ) => Promise<ApiResponse<LocationImageResponse>>;
  getAmenities: () => Promise<ApiResponse<Amenity[]>>;
}

export const LocationService = (
  http: Http = HttpService()
): LocationsRepository => ({
  getLocations: async (query?: ApiQuery) => {
    const res = await http.get<LocationEntity[]>(Endpoints.locations, query);
    const data = res.data?.length
      ? res.data.map((location) => new Location(location))
      : [];
    return { ...res, data };
  },
  getLocation: async (id) => {
    const res = await http.get<LocationEntity>(`${Endpoints.locations}/${id}`);
    const data = new Location(res.data);
    return { ...res, data };
  },
  createLocation: async (params) => {
    const res = await http.post<LocationDTO, LocationEntity>(
      Endpoints.locations,
      params,
      EncodeTypes.MULTIPART
    );
    const data = new Location(res.data);
    return { ...res, data };
  },
  updateLocation: async (id, params) => {
    const res = await http.post<Partial<LocationDTO>, LocationEntity>(
      `${Endpoints.locations}/${id}/update`,
      params,
      EncodeTypes.MULTIPART
    );
    const data = new Location(res.data);
    return { ...res, data };
  },
  addLocationImage: (params) => {
    return http.post<Partial<LocationImageDTO>, LocationImageResponse>(
      Endpoints.location_images,
      params,
      EncodeTypes.MULTIPART
    );
  },
  removeLocationImage: (id) => {
    return http.delete<LocationImageResponse>(
      `${Endpoints.location_images}/${id}`
    );
  },
  getAmenities: async () => {
    const res = await http.get<string[]>(
      `${Endpoints.amenities}`
    );
    const data = res.data.map(r => new Amenity(r))
    return { ...res, data }
  },
});
